/**
 * Conferencia Service - Epic 40: Sistema de Conferência Offline
 *
 * Serviço para upload de Excel, listagem de itens e atualização de quantidades contadas
 */

import apiClient from './apiClient';
import { toast } from 'react-hot-toast';

export interface ItemConferencia {
  id: number;
  codigo_produto: string;
  descricao: string;
  unidade: string;
  lote: string;
  data_validade: string;  // DD/MM/YYYY
  quantidade_sistema: number;  // Qtd Coletada
  quantidade_contada: number | null;
  diferenca: number;
  status_processamento: string;
}

export interface ConferenciaMetadata {
  numero_conferencia?: string;
  oc_conferencia?: string;
  lacres_conferencia?: string;
}

export interface JobBusca {
  job_id: number;
  numero_conferencia: string | null;
  oc_conferencia: string | null;
  lacres_conferencia: string | null;
  total_itens: number;
  cliente_id: number | null;
  cliente_nome: string | null;
  hospital_nome: string | null;
  data_importacao: string | null;
  status: string;
  data_inicio: string | null;
  data_fim: string | null;
  data_prevista: string | null;
}

export interface Cliente {
  id: number;
  nome: string;
  codigo: string;
}

export interface UploadExcelResponse {
  success: boolean;
  message: string;
  job_id: number;
  total_itens: number;
  metadata: ConferenciaMetadata;
}

/**
 * Upload de Excel de conferência do sistema offline
 * Job ID é extraído da célula A1 do Excel
 */
export const uploadConferenciaExcel = async (
  file: File
): Promise<UploadExcelResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<UploadExcelResponse>(
      `/conferencia/upload-excel`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error(response.data.message);
    }

    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao fazer upload do Excel de conferência';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Lista todos os itens de conferência de um job
 */
export const listarItensConferencia = async (jobId: number): Promise<ItemConferencia[]> => {
  try {
    const response = await apiClient.get<ItemConferencia[]>(
      `/conferencia/job/${jobId}/itens`
    );

    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao carregar itens de conferência';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Atualiza a quantidade contada de um item
 * @param silent - Se true, não mostra toast de sucesso (útil para batch saves)
 */
export const atualizarQuantidadeContada = async (
  itemId: number,
  quantidadeContada: number,
  silent: boolean = false
): Promise<ItemConferencia> => {
  try {
    const response = await apiClient.patch<ItemConferencia>(
      `/conferencia/item/${itemId}/contada`,
      { quantidade_contada: quantidadeContada }
    );

    if (!silent) {
      toast.success('Quantidade contada atualizada com sucesso!');
    }
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao atualizar quantidade contada';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Lista TODAS as conferências com filtros opcionais
 */
export const listarTodasConferencias = async (
  filters?: {
    status?: string;
    hospitalId?: number;
    clienteId?: number;
    dataInicio?: string;  // Formato: YYYY-MM-DD
    dataFim?: string;     // Formato: YYYY-MM-DD
    limit?: number;
    offset?: number;
  }
): Promise<JobBusca[]> => {
  try {
    const params: any = {};

    if (filters?.status) params.status = filters.status;
    if (filters?.hospitalId) params.hospital_id = filters.hospitalId;
    if (filters?.clienteId) params.cliente_id = filters.clienteId;
    if (filters?.dataInicio) params.data_inicio = filters.dataInicio;
    if (filters?.dataFim) params.data_fim = filters.dataFim;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;

    const response = await apiClient.get<JobBusca[]>(
      `/conferencia/list`,
      { params }
    );

    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao carregar conferências';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Lista todos os clientes disponíveis (para dropdown)
 */
export const listarClientes = async (): Promise<Cliente[]> => {
  try {
    const response = await apiClient.get<Cliente[]>('/conferencia/clientes');
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao carregar clientes';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Busca conferências por Lacre, O.C. ou ID Contagem
 */
export const buscarConferencias = async (
  tipo: 'lacre' | 'oc' | 'id_contagem',
  valor: string
): Promise<JobBusca[]> => {
  try {
    const response = await apiClient.get<JobBusca[]>(
      `/conferencia/buscar`,
      {
        params: {
          tipo,
          valor,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao buscar conferências';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Adiciona produto não listado na conferência original
 */
export const adicionarItemConferencia = async (
  jobId: number,
  itemData: {
    codigo_produto: string;
    descricao: string;
    unidade: string;
    lote: string;
    data_validade: string; // DD/MM/YYYY
    quantidade_contada: number;
  }
): Promise<ItemConferencia> => {
  try {
    const response = await apiClient.post<ItemConferencia>(
      `/conferencia/job/${jobId}/item`,
      itemData
    );

    // Verificar se houve erro (duplicata)
    if ((response.data as any).success === false) {
      const errorMsg = (response.data as any).message || 'Erro ao adicionar produto';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    toast.success('Produto adicionado com sucesso!');
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao adicionar produto';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Altera status da conferência (PENDENTE → PROCESSADO → ERRO)
 */
export const mudarStatusConferencia = async (
  jobId: number,
  novoStatus: 'PENDENTE' | 'PROCESSADO' | 'ERRO'
): Promise<void> => {
  try {
    const response = await apiClient.patch<{
      success: boolean;
      novo_status: string;
      message?: string;
    }>(
      `/conferencia/job/${jobId}/status`,
      { novo_status: novoStatus }
    );

    if (!response.data.success) {
      const errorMsg = response.data.message || 'Erro ao alterar status';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    toast.success(`Status alterado para ${novoStatus} com sucesso!`);
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao alterar status';
    toast.error(errorMsg);
    throw error;
  }
};

// ========== Conferencia Lacres (novo sistema) ==========

export interface ConferenciaLacreJob {
  job_id: number;
  hospital_nome: string | null;
  cliente_nome: string | null;
  cliente_id: number | null;
  lacres: string | null;
  ordem_coleta: string | null;
  total_itens: number;
  total_conferidos: number;
  total_coletar: number;
  total_conferido_lacre: number;
  status_conferencia: string; // "CONFERIDO" | "NAO_CONFERIDO"
  data_contagem: string | null;
}

/**
 * Lista jobs com conferência de lacres (novo endpoint)
 */
export const listarConferenciaLacreJobs = async (limit = 1000): Promise<ConferenciaLacreJob[]> => {
  try {
    const response = await apiClient.get<ConferenciaLacreJob[]>(
      `/conferencia-lacres/jobs`,
      { params: { limit } }
    );
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao carregar conferências de lacres';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Lista itens de conferência lacre para um job (endpoint existente)
 */
export const listarItensConferenciaLacre = async (jobId: number) => {
  const response = await apiClient.get(`/jobs/${jobId}/conferencia-lacres/items`);
  return response.data;
};

/**
 * Atualiza conferido_lacre de um item (endpoint existente)
 */
export const atualizarConferidoLacre = async (jobId: number, itemId: number, value: number) => {
  const response = await apiClient.patch(
    `/jobs/${jobId}/conferencia-lacres/items/${itemId}`,
    { conferido_lacre: value }
  );
  return response.data;
};

// ========== Conferencia Verificação (novo sistema) ==========

export interface ConferenciaVerificacao {
  id: number;
  numero_conferencia: number | null;
  tipo: 'LACRE' | 'AVULSA';
  job_id: number | null;
  contagem_titulo: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  hospital_nome: string | null;
  ordem_coleta: string | null;
  lacres: string | null;
  avulsa: boolean;
  finalizada: boolean;
  data_conferencia: string | null;
  conferente_nome: string | null;
  com_regime: boolean | null;
  total_itens: number;
  total_conferidos: number;
  notas_fiscais: string;
  status_armazem: string;
  horario_inicio: string | null;
}

export interface ConferenciaVerificacaoDetail {
  id: number;
  numero_conferencia: number | null;
  tipo: 'LACRE' | 'AVULSA';
  job_id: number | null;
  contagem_titulo: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  hospital_id: number | null;
  hospital_nome: string | null;
  ordem_coleta: string | null;
  notas_fiscais: string;
  data_recebimento_nf: string | null;
  devolucao: string;
  status_armazem: string;
  observacoes: string | null;
  finalizada: boolean;
  data_finalizacao: string | null;
  data_conferencia: string | null;
  conferente_nome: string | null;
  com_regime: boolean | null;
  regime_label?: string | null; // 'Sim' | 'Não' | 'Misto' (derivado dos itens)
  total_itens: number;
  total_conferidos: number;
  horario_inicio: string | null;
}

export interface ItemConferenciaVerif {
  id: number;
  codigo_produto: string;
  descricao: string;
  unidade: string;
  lote: string;
  data_validade: string | null;
  lacre_selecionado: string;
  gtin_lido: string;
  quantidade_esperada: number;
  quantidade_conferida: number;
  diferenca: number;
}

export interface LacreDisponivel {
  lacre: string;
  cliente_nome: string | null;
  ordem_coleta: string | null;
  ja_selecionado: boolean;
}

export interface LeituraResult {
  success: boolean;
  message: string;
  item_id: number | null;
  quantidade_conferida: number;
  quantidade_esperada?: number;
  created?: boolean;
  produto_nome?: string | null;
  codigo_produto?: string | null;
  lote?: string | null;
}

/**
 * Lista conferências (verificação) — novo sistema
 */
export const listarConferenciasVerificacao = async (
  params?: { search?: string; cliente_id?: number; finalizada?: boolean }
): Promise<ConferenciaVerificacao[]> => {
  try {
    const response = await apiClient.get<ConferenciaVerificacao[]>(
      '/conferencias/',
      { params }
    );
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao carregar conferências';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Detalhe de uma conferência
 */
export const getConferenciaVerificacao = async (
  id: number
): Promise<ConferenciaVerificacaoDetail> => {
  const response = await apiClient.get<ConferenciaVerificacaoDetail>(
    `/conferencias/${id}/`
  );
  return response.data;
};

/**
 * Criar conferência a partir de job
 */
export const criarConferencia = async (
  jobId: number,
  clienteId?: number
): Promise<{ success: boolean; data: { id: number; numero_conferencia: number }; message: string }> => {
  const response = await apiClient.post('/conferencias/', {
    job_id: jobId,
    cliente_id: clienteId || undefined,
  });
  return response.data;
};

/**
 * Criar conferência avulsa
 */
export const criarConferenciaAvulsa = async (
  clienteId: number,
  hospitalId: number
): Promise<{ success: boolean; data: { id: number; numero_conferencia: number }; message: string }> => {
  const response = await apiClient.post('/conferencias/avulsa/', {
    cliente_id: clienteId,
    hospital_id: hospitalId,
  });
  return response.data;
};

/**
 * Atualizar campos administrativos
 */
export const atualizarConferenciaVerificacao = async (
  id: number,
  data: {
    notas_fiscais?: string;
    data_recebimento_nf?: string;
    devolucao?: string;
    status_armazem?: string;
    observacoes?: string;
  }
): Promise<{ success: boolean }> => {
  const response = await apiClient.patch(`/conferencias/${id}/`, data);
  return response.data;
};

/**
 * Lacres disponíveis
 */
export const getLacresDisponiveis = async (
  conferenciaId: number
): Promise<LacreDisponivel[]> => {
  const response = await apiClient.get<LacreDisponivel[]>(
    `/conferencias/${conferenciaId}/lacres-disponiveis/`
  );
  return response.data;
};

/**
 * Selecionar lacres
 */
export const selecionarLacres = async (
  conferenciaId: number,
  lacres: string[]
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(
    `/conferencias/${conferenciaId}/selecionar-lacres/`,
    { lacres }
  );
  return response.data;
};

/**
 * Listar itens da conferência
 */
export const listarItensConferenciaVerif = async (
  conferenciaId: number
): Promise<ItemConferenciaVerif[]> => {
  const response = await apiClient.get<ItemConferenciaVerif[]>(
    `/conferencias/${conferenciaId}/itens/`
  );
  return response.data;
};

/**
 * Registrar leitura de barcode
 */
export const registrarLeitura = async (
  conferenciaId: number,
  // N92: raw_barcode/scan_type/input_source alimentam a trilha de leituras
  // (debug). Antes o codigo cru so existia no ScanDebugPanel, em memoria.
  data: {
    gtin: string; ref_code?: string; lote?: string; data_validade?: string;
    serial?: string; lacre?: string;
    raw_barcode?: string; input_source?: string; scan_type?: string;
  }
): Promise<LeituraResult> => {
  const response = await apiClient.post<LeituraResult>(
    `/conferencias/${conferenciaId}/leitura/`,
    data
  );
  return response.data;
};

/**
 * Finalizar conferência
 */
export const finalizarConferencia = async (
  conferenciaId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(
    `/conferencias/${conferenciaId}/finalizar/`
  );
  return response.data;
};

/**
 * Toggle conferido (MASTER only)
 */
export const toggleConferido = async (
  conferenciaId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.patch(
    `/conferencias/${conferenciaId}/toggle-conferido/`
  );
  return response.data;
};

/**
 * Atualiza quantidade_conferida de um item da conferência
 * @param silent - Se true, não mostra toast (útil para batch saves)
 */
export const atualizarItemConferencia = async (
  conferenciaId: number,
  itemId: number,
  quantidadeConferida: number,
  silent: boolean = false
): Promise<{ success: boolean; item_id: number; quantidade_conferida: number; diferenca: number }> => {
  try {
    const response = await apiClient.patch(
      `/conferencias/${conferenciaId}/itens/${itemId}/`,
      { quantidade_conferida: quantidadeConferida }
    );
    if (!silent) {
      toast.success('Quantidade conferida atualizada');
    }
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.detail || 'Erro ao atualizar item';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Reinicia (zera) todos os itens da conferência
 */
export const reiniciarConferencia = async (
  conferenciaId: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(
      `/conferencias/${conferenciaId}/reiniciar/`
    );
    if (response.data.success) {
      toast.success(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.detail || 'Erro ao reiniciar conferência';
    toast.error(errorMsg);
    throw error;
  }
};

// ========== Relatórios PDF de Conferência ==========

/**
 * Download PDF de Conferências Avulsas (por cliente + período)
 */
/**
 * N167: MESMO relatório de Conferências Avulsas, em Excel.
 *
 * Existe porque no PDF não dá para somar quantidade nem filtrar. O backend usa a
 * mesma query do PDF (`_linhas_conferencias_avulsas`), então os dois não divergem.
 */
export const downloadConferenciasAvulsasExcel = async (
  clienteId: number,
  dataInicio: string,
  dataFim: string
): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/relatorios/conferencias-avulsas/excel`,
      {
        params: { cliente_id: clienteId, data_inicio: dataInicio, data_fim: dataFim },
        responseType: 'blob',
      }
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `conferencias-avulsas-${dataInicio}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao gerar Excel de conferências avulsas';
    toast.error(errorMsg);
    throw error;
  }
};

export const downloadConferenciasAvulsasPdf = async (
  clienteId: number,
  dataInicio: string,
  dataFim: string
): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/relatorios/conferencias-avulsas`,
      {
        params: { cliente_id: clienteId, data_inicio: dataInicio, data_fim: dataFim },
        responseType: 'blob',
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `conferencias-avulsas-${dataInicio}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao gerar PDF de conferências avulsas';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * N167: a MESMA conferência de coleta, em Excel.
 *
 * Em planilha dá para somar "A Coletar" x "Coletado" e filtrar as linhas com
 * diferença — o uso real do relatório. Backend usa a mesma extração do PDF
 * (`_linhas_conferencia_coleta`), então os dois não divergem.
 */
export const downloadConferenciaColetaExcel = async (
  conferenciaId: number
): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/relatorios/conferencia-coleta/${conferenciaId}/excel`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `conferencia-coleta-${conferenciaId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao gerar Excel da conferência de coleta';
    toast.error(errorMsg);
    throw error;
  }
};

/**
 * Download PDF de Conferência de Coleta (por conferência ID)
 */
export const downloadConferenciaColetaPdf = async (
  conferenciaId: number
): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/relatorios/conferencia-coleta/${conferenciaId}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `conferencia-coleta-${conferenciaId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || 'Erro ao gerar PDF da conferência de coleta';
    toast.error(errorMsg);
    throw error;
  }
};

export default {
  uploadConferenciaExcel,
  listarItensConferencia,
  atualizarQuantidadeContada,
  adicionarItemConferencia,
  mudarStatusConferencia,
  listarTodasConferencias,
  buscarConferencias,
  listarClientes,
  listarConferenciaLacreJobs,
  listarItensConferenciaLacre,
  atualizarConferidoLacre,
  // Novo sistema
  listarConferenciasVerificacao,
  getConferenciaVerificacao,
  criarConferencia,
  criarConferenciaAvulsa,
  atualizarConferenciaVerificacao,
  getLacresDisponiveis,
  selecionarLacres,
  listarItensConferenciaVerif,
  registrarLeitura,
  finalizarConferencia,
  toggleConferido,
  atualizarItemConferencia,
  reiniciarConferencia,
  // Relatórios PDF
  downloadConferenciasAvulsasPdf,
  downloadConferenciaColetaPdf,
  downloadConferenciaColetaExcel,
};
